import React, { useMemo, useState, useCallback, useEffect } from 'react'
import {
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Button,
  Skeleton,
  SkeletonCircle,
  useDisclosure,
} from '@chakra-ui/react'
import FastAvatar from '../image/FastAvatar'
import { CircleFlag } from 'react-circle-flags'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import OrganizationModalCard from './OrganizationModalCard'
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { fetchTeamList, fetchWorkforceRoster } from '../../api/workforce/workforce'
import FilterButton from './base/FilterButton'
import SearchInput from './base/SearchInput'
import Pagination from './base/Pagination'
import { Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react'
import { ChevronDownIcon } from '../../assets/icons'
import { useTranslation } from 'react-i18next'
import { Mail, Sparkles, UserRound } from 'lucide-react'

interface OrganizationPerson {
  id: string
  first_name?: string
  last_name?: string
  profile_picture?: string
  current_position?: { id: string; title: string }
  country_of_residence?: string
  country_of_work?: string
  work_email?: string
  primary_evaluator?: {
    id: string
    first_name?: string
    last_name?: string
    profile_picture?: string
  }
}

interface OrganizationCardProps {
  person: OrganizationPerson
  onClick: (person: OrganizationPerson) => void
}

const OrganizationCard: React.FC<OrganizationCardProps> = ({ person, onClick }) => {
  const { t } = useTranslation()
  const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim()
  const roleLabel = person.current_position?.title || t('common.labels.unknownPosition')
  const workCountryLabel = person.country_of_work
    ? getCountryNameByCode(person.country_of_work) || person.country_of_work
    : t('common.labels.notSpecified')
  const managerName = person.primary_evaluator
    ? `${person.primary_evaluator.first_name || ''} ${person.primary_evaluator.last_name || ''}`.trim()
    : t('common.labels.notSpecified')

  return (
    <Box
      borderWidth="1px"
      borderColor="solid.50"
      p={0}
      bg="white"
      boxShadow="0 8px 24px rgba(15,23,42,0.04)"
      borderRadius="18px"
      overflow="hidden"
      transition="all 180ms ease"
      _hover={{
        boxShadow: '0 18px 38px rgba(15,23,42,0.10)',
        transform: 'translateY(-4px)',
        borderColor: 'solid.100',
        cursor: 'pointer',
      }}
      onClick={() => onClick(person)}
      minH="228px"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <Box
        px={5}
        pt={5}
        pb={4}
        bg="linear-gradient(180deg, var(--chakra-colors-primary-0) 0%, rgba(255,255,255,0) 100%)"
      >
        <Flex align="flex-start" justify="space-between" gap={3}>
          <HStack spacing={3} align="center" minW={0}>
            <Box
              borderRadius="full"
              border="3px solid"
              borderColor="solid.100"
              overflow="hidden"
              boxShadow="0 10px 24px rgba(15,23,42,0.12)"
              bg="solid.0"
              flexShrink={0}
            >
              <FastAvatar
                src={person.profile_picture || ''}
                name={fullName}
                size="lg"
                bg="solid.200"
                color="solid.700"
              />
            </Box>
            <VStack align="start" spacing={1} flex="1" minW="0">
              <Text fontWeight="700" fontSize="16px" color="solid.900" noOfLines={1} width="100%">
                {fullName}
              </Text>
              <Text color="solid.600" fontSize="13px" noOfLines={2} width="100%" lineHeight="1.45">
                {roleLabel}
              </Text>
            </VStack>
          </HStack>

        </Flex>
      </Box>

      <VStack align="stretch" spacing={3} px={5} pb={5} flex="1">
        <HStack spacing={2} flexWrap="wrap">
          {person.country_of_work ? (
            <HStack
              spacing="8px"
              px="10px"
              py="6px"
              borderRadius="999px"
              bg="solid.0"
              border="1px solid"
              borderColor="solid.50"
            >
              <CircleFlag
                countryCode={person.country_of_work.toLowerCase()}
                style={{ width: 16, height: 16 }}
              />
              <Text fontSize="12px" fontWeight="600" color="solid.700">
                {workCountryLabel}
              </Text>
            </HStack>
          ) : (
            <HStack
              spacing="8px"
              px="10px"
              py="6px"
              borderRadius="999px"
              bg="solid.25"
              border="1px solid"
              borderColor="solid.50"
            >
              <Icon as={Sparkles} boxSize="12px" color="solid.400" />
              <Text fontSize="12px" fontWeight="600" color="solid.500">
                {t('common.labels.notSpecified')}
              </Text>
            </HStack>
          )}
        </HStack>

        <VStack align="stretch" spacing={2.5} flex="1">
          <Flex
            align="flex-start"
            gap={2.5}
            p="10px 12px"
            borderRadius="12px"
            bg="solid.0"
            border="1px solid"
            borderColor="solid.50"
          >
            <Icon as={Mail} boxSize="14px" color="solid.500" mt="3px" />
            <Box minW={0}>
              <HStack spacing="6px" align="center">
                <Text fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.4px">
                  {t('common.fields.email')}
                </Text>
              </HStack>
              <Text fontSize="13px" color="solid.800" mt="2px" noOfLines={1} title={person.work_email || ''}>
                {person.work_email || t('common.labels.na')}
              </Text>
            </Box>
          </Flex>

          <Flex
            align="flex-start"
            gap={2.5}
            p="10px 12px"
            borderRadius="12px"
            bg="solid.0"
            border="1px solid"
            borderColor="solid.50"
          >
            <Icon as={UserRound} boxSize="14px" color="solid.500" mt="3px" />
            <Box minW={0}>
              <HStack spacing="6px" align="center">
                <Text fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.4px">
                  {t('common.fields.manager')}
                </Text>
              </HStack>
              <HStack spacing={2} mt="4px" minW={0}>
                {person.primary_evaluator && (
                  <Box
                    borderRadius="full"
                    border="1px solid"
                    borderColor="solid.100"
                    overflow="hidden"
                    bg="solid.0"
                    flexShrink={0}
                  >
                    <FastAvatar
                      src={person.primary_evaluator.profile_picture || ''}
                      name={managerName}
                      size="xs"
                      bg="solid.200"
                      color="solid.700"
                    />
                  </Box>
                )}
                <Text fontSize="13px" color="solid.800" noOfLines={1}>
                  {managerName}
                </Text>
              </HStack>
            </Box>
          </Flex>
        </VStack>

        <Flex justify="flex-end" align="center" pt={1}>
          <Text fontSize="12px" fontWeight="700" color="primary.200">
            {t('common.actions.view')}
          </Text>
        </Flex>
      </VStack>
    </Box>
  )
}

const WorkforceOrganizationTable = () => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const [showOnlyManagers, setShowOnlyManagers] = useState(false)
  const [sortBy, setSortBy] = useState<'recent' | 'alpha_asc' | 'alpha_desc'>('recent')
  const SORT_OPTIONS = useMemo(
    () =>
      [
        { id: 'recent', label: t('workforce.organization.sortOptions.recentlyAdded') },
        { id: 'alpha_asc', label: t('workforce.organization.sortOptions.alphabeticalAZ') },
        { id: 'alpha_desc', label: t('workforce.organization.sortOptions.alphabeticalZA') },
      ] as const,
    [t],
  )

  const [page, setPage] = useState(1)
  const itemsPerPage = 20
  const [expandedPerson, setExpandedPerson] = useState<OrganizationPerson | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()

  // --- Filter state ---
  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // --- Infinite scroll filter data: Teams ---
  const [teamsFilterSearch, setTeamsFilterSearch] = useState('')
  const teamsFilterQuery = useInfiniteQuery({
    queryKey: ['filter-teams', 10, teamsFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchTeamList({
        search: teamsFilterSearch,
        page: pageParam,
        pageSize: 10,
        filters: {},
        fields: 'id,name',
        expand: '',
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
  })
  const teamsFilterList = useMemo(
    () =>
      (teamsFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string; name: string }[] }) =>
        Array.isArray(p?.data) ? p.data.map((team) => ({ id: team.id, name: team.name })) : [],
      ),
    [teamsFilterQuery.data?.pages],
  )

  const countryFilterList = useMemo(() => phoneNumbers.map((c) => ({ id: c.code, name: c.name })), [])

  const ordering =
    sortBy === 'recent' ? '-created_at' : sortBy === 'alpha_asc' ? 'first_name,last_name' : '-first_name,-last_name'

  // return to page 1 whenever searchQuery, filters, sortBy, or showOnlyManagers changes
  React.useEffect(() => {
    setPage(1)
  }, [searchQuery, filters, sortBy, showOnlyManagers])

  const { data, isLoading } = useQuery({
    queryKey: ['workforce-roster', searchQuery, filters, sortBy, page, showOnlyManagers],
    queryFn: () =>
      fetchWorkforceRoster({
        search: searchQuery,
        page,
        pageSize: itemsPerPage,
        filters: {
          ...filters,
          ...(showOnlyManagers ? { only_primary_evaluators: ['true'] } : {}),
        },
        ordering,
        expand: 'current_position,primary_evaluator',
        fields:
          'id,first_name,last_name,full_name,profile_picture,current_position.id,current_position.title,country_of_work,work_email,primary_evaluator,country_of_residence',
      }),
    refetchOnWindowFocus: false,
    staleTime: 30000,
  })

  const queryClient = useQueryClient()

  // Prefetch next employees page when current page looks full
  useEffect(() => {
    const currentCount = data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['workforce-roster', searchQuery, filters, sortBy, nextPage, showOnlyManagers],
      staleTime: 30000,
      queryFn: () =>
        fetchWorkforceRoster({
          search: searchQuery,
          page: nextPage,
          pageSize: itemsPerPage,
          filters: {
            ...filters,
            ...(showOnlyManagers ? { only_primary_evaluators: ['true'] } : {}),
          },
          ordering,
          expand: 'current_position',
          fields:
            'id,first_name,last_name,full_name,profile_picture,current_position.id,current_position.title,country_of_work,work_email',
        }),
    })
  }, [data, page, searchQuery, filters, sortBy, showOnlyManagers, queryClient, itemsPerPage])

  const handleSearch = useCallback((value: string) => {
    setPage(1)
    setSearchQuery(value)
  }, [])

  const handleRowClick = (person: OrganizationPerson) => {
    setExpandedPerson(person)
    onOpen()
  }

  const displayData = data?.data || []

  return (
    <Card width="100%" p="0">
      <Flex align="center" gap={0} wrap="wrap" mb={4} pr={{ base: 0, sm: 4 }}>
        <Box flex="1">
          <Flex
            justifyContent="space-between"
            alignItems={{ base: 'stretch', sm: 'center' }}
            direction={{ base: 'column', sm: 'row' }}
            height={{ base: 'auto', sm: '64px' }}
            flexWrap="wrap"
            gap={{ base: 3, sm: 4 }}
            px={{ base: '12px', sm: '20px' }}
            py={{ base: '12px', sm: 0 }}
          >
            <SearchInput onSearch={handleSearch} />
            <Flex display={{ base: 'none', md: 'flex' }} gap="8px" align="center" wrap="wrap" justify="flex-end" sx={{ '& > *': { flex: '0 0 auto' } }}>
              <FilterButton
                label={t('common.fields.team')}
                options={teamsFilterList}
                onApply={(selected) => updateFilter('teams', selected)}
                search={teamsFilterSearch}
                setSearch={setTeamsFilterSearch}
                onLoadMore={() => teamsFilterQuery.fetchNextPage()}
                hasMore={!!teamsFilterQuery.hasNextPage}
                isLoadingMore={teamsFilterQuery.isFetchingNextPage}
                isLoading={teamsFilterQuery.isFetching && !teamsFilterQuery.isFetchingNextPage}
              />
              <FilterButton
                label={t('common.fields.countryOfResidence')}
                options={countryFilterList}
                onApply={(selected) => {
                  updateFilter('country', selected)
                  updateFilter('country_type', ['residence'])
                }}
              />
              <Flex 
                align="center" 
                gap="10px" 
                px="14px" 
                h="34px"
                justifyContent="space-between"
                borderRadius="10px" 
                bg={showOnlyManagers ? 'purple.400' : 'white'}
                border="1px solid"
                borderColor={showOnlyManagers ? 'purple.400' : 'solid.100'}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => setShowOnlyManagers(!showOnlyManagers)}
                _hover={{ 
                  bg: showOnlyManagers ? 'purple.500' : 'solid.25',
                  borderColor: showOnlyManagers ? 'purple.500' : 'solid.200'
                }}
              >
                <Text 
                  fontSize="13px" 
                  color={showOnlyManagers ? 'white' : 'black'}
                  transition="color 0.2s"
                >
                  {t('common.fields.managersOnly', 'Managers Only')}
                </Text>
                <Box
                  w="32px"
                  h="18px"
                  borderRadius="full"
                  bg={showOnlyManagers ? 'rgba(255,255,255,0.3)' : 'solid.200'}
                  position="relative"
                  transition="background 0.2s"
                  flexShrink={0}
                >
                  <Box
                    position="absolute"
                    top="2px"
                    left={showOnlyManagers ? '16px' : '2px'}
                    w="14px"
                    h="14px"
                    borderRadius="full"
                    bg="white"
                    transition="all 0.2s"
                    boxShadow="0 1px 3px rgba(0,0,0,0.2)"
                  />
                </Box>
              </Flex>
              <Menu placement="bottom-end">
                <MenuButton
                  as={Button}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  ml="auto"
                  variant="white"
                  size="sm"
                  h="34px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="10px"
                >
                  {SORT_OPTIONS.find((o) => o.id === sortBy)?.label ?? t('common.actions.sort')}
                  <Icon width="12px" height="12px" fill="none" color="solid.500" ml={1.5}>
                    <ChevronDownIcon />
                  </Icon>
                </MenuButton>

                <MenuList minW="220px">
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.id}
                      onClick={() => setSortBy(opt.id)}
                      fontWeight={sortBy === opt.id ? '600' : '400'}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          </Flex>
        </Box>
      </Flex>

      <SimpleGrid
        templateColumns={{
          base: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        }}
        gap={6}
        p={6}
      >
        {isLoading ? (
          Array.from({ length: itemsPerPage }).map((_, index) => (
            <Box
              key={index}
              borderWidth="1px"
              borderColor="solid.50"
              p={4}
              bg="white"
              boxShadow="sm"
              borderRadius="12px"
              minH="200px"
            >
              <Flex align="center" mb={2}>
                <SkeletonCircle size="16" mr={3} />
                <VStack align="start" spacing={2} flex="1">
                  <Skeleton height="16px" width="60%" />
                  <Skeleton height="14px" width="80%" />
                </VStack>
              </Flex>
              <VStack align="start" spacing={2} mt={4}>
                <Skeleton height="14px" width="90%" />
                <Skeleton height="14px" width="70%" />
                <Skeleton height="14px" width="85%" />
              </VStack>
            </Box>
          ))
        ) : displayData.length > 0 ? (
          displayData.map((person: OrganizationPerson) => (
            <OrganizationCard key={person.id} person={person} onClick={handleRowClick} />
          ))
        ) : (
          <Text>{t('common.labels.noEmployeesFound')}</Text>
        )}

        {/* Expanded Organization Modal */}
        <OrganizationModalCard
          person={expandedPerson}
          isOpen={isOpen}
          onClose={() => {
            setExpandedPerson(null)
            onClose()
          }}
        />
      </SimpleGrid>

      <Pagination
        currentPage={page}
        itemsPerPage={itemsPerPage}
        maxPage={data?.max_page ?? 1}
        totalItems={data?.count ?? 0}
        handlePreviousPage={() => setPage((p) => Math.max(1, p - 1))}
        handleNextPage={() => {
          if (data?.has_next) setPage((p) => p + 1)
        }}
        handlePageClick={(pageNumber: number) => setPage(pageNumber)}
      />
    </Card>
  )
}

export default WorkforceOrganizationTable
